import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../cart/CartContext";
import { useAuth } from "../auth/AuthContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { pathFor } from "../app/paths";

export function Header() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const { totalItems, toggleCart } = useCart();
  const { user } = useAuth();
  const cfg = useSiteConfig();
  const isMobile = useIsMobile();
  const location = useLocation();
  const base = `/${locale || "it"}`;
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { key: "nav.home", to: base, match: [base, base + "/"] },
    { key: "nav.collezioni", to: pathFor("collections", locale), match: [pathFor("collections", locale), `${base}/collections`] },
    { key: "nav.blog", to: pathFor("blog", locale), match: [pathFor("blog", locale)] },
    { key: "nav.about", to: pathFor("about", locale), match: [pathFor("about", locale)] },
    { key: "nav.fiscalita", to: pathFor("fiscal", locale), match: [pathFor("fiscal", locale)] },
    { key: "nav.contatti", to: pathFor("contact", locale), match: [pathFor("contact", locale)] },
  ];

  const isActive = (item: typeof navItems[0]) =>
    item.match.some((m) => location.pathname === m || (m !== base && location.pathname.startsWith(m + "/")));

  return (
    <header
      style={{
        background: scrolled ? cfg.color_body_bg : "transparent",
        padding: isMobile ? "12px 20px" : "0 48px",
        position: "sticky",
        top: 0,
        zIndex: 100,
        borderBottom: scrolled ? "1px solid rgba(28,75,59,0.89)" : "1px solid transparent",
        transition: "background 0.3s ease, border-color 0.3s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          maxWidth: 1200,
          margin: "0 auto",
          height: isMobile ? "auto" : 72,
        }}
      >
        {/* Logo */}
        <Link
          to={base}
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            minWidth: isMobile ? undefined : 160,
          }}
        >
          <img
            src={cfg.logo_url ? resolveImageUrl(cfg.logo_url) : "/granborgo-logo.svg"}
            alt={cfg.brand_name || t("header.brandName")}
            style={{ height: isMobile ? 32 : 40, objectFit: "contain" }}
          />
        </Link>

        {/* Desktop Navigation */}
        {!isMobile && (
          <nav style={{ display: "flex", gap: 32, alignItems: "center" }}>
            {navItems.map((item) => {
              const active = isActive(item);
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  style={{
                    color: cfg.color_dark,
                    textDecoration: "none",
                    fontSize: 12,
                    fontWeight: active ? 500 : 400,
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                    transition: "opacity 0.2s ease",
                    opacity: active ? 1 : 0.7,
                    borderBottom: active ? `1.5px solid ${cfg.color_dark}` : "1.5px solid transparent",
                    paddingBottom: 4,
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.opacity = "1"; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.opacity = "0.7"; }}
                >
                  {t(item.key)}
                </Link>
              );
            })}
          </nav>
        )}

        {/* Right side */}
        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 12 : 16, minWidth: isMobile ? undefined : 160, justifyContent: "flex-end" }}>
          {!isMobile && <LanguageSwitcher />}

          <button
            onClick={toggleCart}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              position: "relative",
              padding: 4,
            }}
            aria-label={t("a11y.cart")}
            /* Aggancio stabile per i test: l'aria-label è tradotto e cambia con
               la lingua, quindi non è un selettore affidabile. */
            data-gb="cart"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={cfg.color_dark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
            {totalItems > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -2,
                  right: -6,
                  background: cfg.color_dark,
                  color: "#fff",
                  fontSize: 9,
                  fontWeight: 500,
                  borderRadius: "50%",
                  width: 16,
                  height: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                }}
              >
                {totalItems}
              </span>
            )}
          </button>

          <Link
            to={user ? `${base}/account` : `${base}/login`}
            style={{ display: "flex", padding: 4 }}
            aria-label={user ? t("account.title") : t("nav.login")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={cfg.color_dark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </Link>

          {isMobile && (
            <button
              onClick={() => setMenuOpen(true)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}
              aria-label={t("a11y.menu")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={cfg.color_dark} strokeWidth="1.5" strokeLinecap="round">
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="16" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Mobile nav */}
      {isMobile && menuOpen && (
        <>
          <div className="mobile-nav-overlay" onClick={() => setMenuOpen(false)} />
          <div className="mobile-nav-panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid rgba(28,75,59,0.89)" }}>
              <span style={{ fontFamily: cfg.font_heading, fontSize: 18, fontWeight: 400, color: cfg.color_dark, letterSpacing: 0.5 }}>
                {cfg.brand_name}
              </span>
              <button
                onClick={() => setMenuOpen(false)}
                style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", padding: 4, lineHeight: 1, color: cfg.color_dark }}
              >
                &times;
              </button>
            </div>
            <nav style={{ flex: 1, display: "flex", flexDirection: "column", padding: "16px 0" }}>
              {navItems.map((item) => {
                const active = isActive(item);
                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    style={{
                      color: cfg.color_dark,
                      textDecoration: "none",
                      fontSize: 15,
                      fontWeight: active ? 500 : 400,
                      letterSpacing: 0.8,
                      textTransform: "uppercase",
                      padding: "14px 24px",
                      borderLeft: active ? `3px solid ${cfg.color_dark}` : "3px solid transparent",
                      background: active ? cfg.color_light_bg : "transparent",
                    }}
                  >
                    {t(item.key)}
                  </Link>
                );
              })}
            </nav>
            <div style={{ padding: "16px 24px", borderTop: "1px solid rgba(28,75,59,0.89)" }}>
              {/* In fondo al pannello: l'elenco deve aprirsi verso l'alto, altrimenti esce dallo schermo */}
              <LanguageSwitcher dropUp />
            </div>
          </div>
        </>
      )}
    </header>
  );
}
